import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createChatSocket } from "../utils/chatSocket";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Stack,
  CircularProgress,
  Tabs,
  Tab,
  Fab,
  Snackbar,
} from "@mui/material";
import { green } from "@mui/material/colors";
import LogoutIcon from "@mui/icons-material/Logout";
import SendIcon from "@mui/icons-material/Send";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import UploadIcon from "@mui/icons-material/Upload";
import FileUpload from "../components/Contents/Fileuplad";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  TableContainer,
} from "@mui/material";
import {
  locationFromAttendanceRow,
  parseSupportAttendQuery,
} from "../lib/rdvBulkInput/supportAppLink";

const socket = createChatSocket();
const RDV_ATTEND_STORAGE_KEY = "gwmfm_rdv_attend";
const CSORT_SUPPORT = 2;

const steps = {
  ask: "ask",
  fireForm: "fireForm",
  otherForm: "otherForm",
  done: "done",
};

export default function RdvAttendPage(props) {
  const { apiAddr } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const query = useMemo(
    () => parseSupportAttendQuery(searchParams, location.pathname),
    [searchParams, location.pathname],
  );
  const typeParam = query.type || (query.att ? "other" : "");
  const dsid = query.dsid;
  const rdvId = query.rdvId;
  const siteId = query.siteId;
  const isSiteMode = query.isSiteMode;
  const dsnm = query.dsnm;
  const rname = query.rname;
  const attParam = query.att;

  const [resolvedLoc, setResolvedLoc] = useState(null);
  const effectiveIsSiteMode = resolvedLoc?.isSiteMode ?? isSiteMode;
  const effectiveRdvId = resolvedLoc?.rdvId ?? rdvId;
  const effectiveSiteId = resolvedLoc?.siteId ?? siteId;
  const locationId = effectiveIsSiteMode ? effectiveSiteId : effectiveRdvId;
  const locationIdKey = effectiveIsSiteMode ? "site_id" : "rdv_id";

  const [step, setStep] = useState(steps.ask);
  const [pname, setPname] = useState("");
  const [porg, setPorg] = useState("");
  const [pdept, setPdept] = useState("");
  const [prank, setPrank] = useState("");
  const [insaCode, setInsaCode] = useState("");
  const [pphone, setPphone] = useState("");
  const [dutyDesc, setDutyDesc] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [completedAction, setCompletedAction] = useState(""); // "checkin" | "checkout"
  const [activeTab, setActiveTab] = useState(0); // 0: 등록, 1: 대화방, 2: 등록이력 (type=other 일 때만 사용)
  const [chatDisplayName, setChatDisplayName] = useState(""); // 등록 완료 후 대화명 (기관 부서 성명)
  const [supportChatMessages, setSupportChatMessages] = useState([]);
  const [supportChatInput, setSupportChatInput] = useState("");
  const [supportFilename, setSupportFilename] = useState("");
  const [supportInputClicked, setSupportInputClicked] = useState(0);
  const [supportSendClicked, setSupportSendClicked] = useState(0);
  const supportChatListRef = useRef(null);
  const autoLoadDoneRef = useRef(false); // step=done 진입 시 자동 로드 중복 방지
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [favSnackOpen, setFavSnackOpen] = useState(false);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [prefillFailed, setPrefillFailed] = useState(false);
  const [alreadyCheckedIn, setAlreadyCheckedIn] = useState(false);
  const [alreadyCheckedOut, setAlreadyCheckedOut] = useState(false);

  // rdv_id/site_id, att-only(d+a), 또는 dsid+a(구버전+연동) 허용
  const isValid =
    dsid.trim() !== "" &&
    (query.isAttOnlyMode ||
      Boolean(String(attParam || "").trim() && ((rdvId != null && rdvId > 0) || (siteId != null && siteId > 0))) ||
      (rdvId != null && rdvId > 0) ||
      (siteId != null && siteId > 0));
  const locationReady =
    !query.isAttOnlyMode || resolvedLoc != null;

  const handleAddToFavorites = () => {
    const url = window.location.href;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(() => setFavSnackOpen(true)).catch(() => setFavSnackOpen(true));
    } else {
      setFavSnackOpen(true);
    }
  };

  const applyAttendancePrefill = useCallback(
    (row) => {
      setResolvedLoc(locationFromAttendanceRow(row));
      const personType = row.person_type === "fire" ? "fire" : "other";
      setPname(String(row.pname || ""));
      setPorg(String(row.porg || ""));
      setPdept(String(row.pdept || ""));
      setPrank(String(row.prank || ""));
      setPphone(String(row.pphone || ""));
      setDutyDesc(String(row.duty_desc || ""));
      if (row.insa_code) setInsaCode(String(row.insa_code));

      const checkedIn = Boolean(row.check_in_dt);
      const checkedOut = Boolean(row.check_out_dt);
      setAlreadyCheckedIn(checkedIn && !checkedOut);
      setAlreadyCheckedOut(checkedIn && checkedOut);

      if (personType === "other") {
        const displayName =
          [row.porg, row.pdept, row.pname]
            .map((s) => String(s || "").trim())
            .filter(Boolean)
            .join(" ") ||
          String(row.pname || "").trim() ||
          "지원인력";
        setChatDisplayName(displayName);
        setStep(steps.otherForm);
      } else {
        setStep(steps.fireForm);
      }

      if (checkedIn) {
        setCompletedAction(checkedOut ? "checkout" : "checkin");
      }

      try {
        const loc = locationFromAttendanceRow(row);
        const locId = loc.isSiteMode ? loc.siteId : loc.rdvId;
        const locKey = loc.isSiteMode ? "site_id" : "rdv_id";
        localStorage.setItem(
          RDV_ATTEND_STORAGE_KEY,
          JSON.stringify({
            dsid,
            [locKey]: locId,
            dsnm,
            rname,
            typeParam,
            person_type: personType,
            pname: String(row.pname || "").trim(),
            pphone: String(row.pphone || "").trim(),
            porg: String(row.porg || "").trim(),
            pdept: String(row.pdept || "").trim(),
            prank: String(row.prank || "").trim(),
            insa_code: String(row.insa_code || "").trim(),
            duty_desc: String(row.duty_desc || "").trim(),
            chatDisplayName:
              personType === "other"
                ? [row.porg, row.pdept, row.pname]
                    .map((s) => String(s || "").trim())
                    .filter(Boolean)
                    .join(" ") || String(row.pname || "").trim()
                : "",
            completedAction: checkedIn
              ? checkedOut
                ? "checkout"
                : "checkin"
              : "",
          }),
        );
      } catch (e) {
        console.warn("rdv attend save failed", e);
      }
    },
    [dsid, dsnm, rname, typeParam],
  );

  // localStorage 복원: 동일 dsid+rdv_id 이력이 있으면 출근 완료 상태로 복원
  useEffect(() => {
    if (!isValid || typeof localStorage === "undefined" || attParam) return;
    try {
      const raw = localStorage.getItem(RDV_ATTEND_STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved.dsid !== dsid || saved[locationIdKey] !== locationId) return;
      setStep(steps.done);
      setCompletedAction("checkin");
      if (saved.pname) setPname(saved.pname);
      if (saved.pphone) setPphone(saved.pphone);
      if (saved.porg) setPorg(saved.porg);
      if (saved.pdept) setPdept(saved.pdept);
      if (saved.prank) setPrank(saved.prank);
      if (saved.insa_code) setInsaCode(saved.insa_code);
      if (saved.duty_desc) setDutyDesc(saved.duty_desc);
      if (saved.chatDisplayName) setChatDisplayName(saved.chatDisplayName);
      if (saved.completedAction) setCompletedAction(saved.completedAction);
    } catch (e) {
      console.warn("rdv attend restore failed", e);
    }
  }, [isValid, dsid, rdvId, typeParam, attParam, locationId, locationIdKey, effectiveIsSiteMode]);

  useEffect(() => {
    if (isValid && typeParam === "other" && step === steps.ask) {
      setStep(steps.otherForm);
    }
  }, [isValid, typeParam, step]);

  // SMS 링크 att/a=snum — 서버 등록정보 불러와 폼·집결지 ID 연동
  useEffect(() => {
    if (!isValid || !apiAddr || !dsid || !attParam) return;
    let cancelled = false;
    setPrefillLoading(true);
    setPrefillFailed(false);
    (async () => {
      try {
        const base = String(apiAddr).replace(/\/$/, "");
        const res = await fetch(
          `${base}/get/rdvattendance?dsid=${encodeURIComponent(dsid)}&snum=${encodeURIComponent(attParam)}`,
        );
        const list = await res.json();
        if (cancelled) return;
        if (!Array.isArray(list) || !list[0]) {
          setPrefillFailed(true);
          return;
        }
        applyAttendancePrefill(list[0]);
      } catch (e) {
        console.warn("attendance prefill failed", e);
        if (!cancelled) setPrefillFailed(true);
      } finally {
        if (!cancelled) setPrefillLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isValid, apiAddr, dsid, attParam, applyAttendancePrefill]);

  // 지원인력 대화방: 메시지 수신 (dsid 일치 시만)
  useEffect(() => {
    const handler = (data) => {
      if (data.dsid !== dsid) return;
      setSupportChatMessages((prev) => [
        ...prev,
        { name: data.name, msg: data.msg, time: data.time, time2: data.time2 || 0, filename: data.filename || "" },
      ]);
    };
    socket.on("chatting_support", handler);
    return () => socket.off("chatting_support", handler);
  }, [dsid]);

  // 지원인력 대화방: 초기 메시지 로드 (대화방 탭 활성화 시)
  const loadSupportChat = async () => {
    if (!apiAddr || !dsid) return;
    try {
      const res = await fetch(
        `${apiAddr}/get/getchatmsg?dsid=${encodeURIComponent(dsid)}&latesnum=0&csort=${CSORT_SUPPORT}`
      );
      const list = await res.json();
      if (Array.isArray(list)) {
        // 백엔드가 DESC(최신순) 반환 → 역순으로 표시 (오래된 것이 위)
        const sorted = [...list].reverse();
        setSupportChatMessages(
          sorted.map((r) => ({
            name: r.cname || "",
            msg: r.cmsg || "",
            time: r.cipdt ? new Date(r.cipdt).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit", hour12: true }) : "",
            time2: r.snum || 0,
            filename: r.cfilename || "",
          }))
        );
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (typeParam === "other" && chatDisplayName && activeTab === 1) loadSupportChat();
  }, [typeParam, chatDisplayName, activeTab, dsid, apiAddr]);

  // 모바일 지원인력 대화방에서 파일 업로드 시 sessionStorage 설정 (FileUpload 컴포넌트에서 사용)
  useEffect(() => {
    if (activeTab !== 1 || !dsid) return;
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.ondsid = dsid;
      sessionStorage.chatname = chatDisplayName || "";
    }
  }, [activeTab, dsid, chatDisplayName]);

  // 등록이력: 해당 집결지/재난현장 출퇴근 목록 조회 후 현재 사용자(pname+pphone 또는 insa_code)로 필터
  const loadAttendanceHistory = useCallback(async () => {
    if (!apiAddr || !dsid || locationId == null) return;
    setHistoryLoading(true);
    try {
      // site 모드: site_id 파라미터 사용 / 일반: rdv_id 사용
      const locParam = effectiveIsSiteMode
        ? `site_id=${locationId}`
        : `rdv_id=${locationId}`;
      const res = await fetch(
        `${apiAddr}/get/rdvattendance?dsid=${encodeURIComponent(dsid)}&${locParam}`
      );
      const list = await res.json();
      if (!Array.isArray(list)) {
        setAttendanceHistory([]);
        return;
      }
      const pnameTrim = (pname || "").trim();
      const pphoneTrim = (pphone || "").trim();
      const insaTrim = (insaCode || "").trim();
      const filtered =
        typeParam === "other" || step === steps.otherForm
          ? list.filter(
              (r) =>
                r.person_type === "other" &&
                String(r.pname || "").trim() === pnameTrim &&
                String(r.pphone || "").trim() === pphoneTrim
            )
          : list.filter(
              (r) =>
                r.person_type === "fire" &&
                (insaTrim
                  ? String(r.insa_code || "").trim() === insaTrim
                  : String(r.pname || "").trim() === pnameTrim && String(r.pphone || "").trim() === pphoneTrim)
            );
      setAttendanceHistory(filtered);
    } catch (e) {
      console.error(e);
      setAttendanceHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [apiAddr, dsid, rdvId, typeParam, step, pname, pphone, insaCode]);

  useEffect(() => {
    if (typeParam === "other" && activeTab === 2) loadAttendanceHistory();
  }, [activeTab, typeParam, loadAttendanceHistory]);

  // step=done 진입 시 대화방·등록이력 자동 로드 (type=other 전용, 한 번만)
  useEffect(() => {
    if (step !== steps.done || typeParam !== "other" || !apiAddr) {
      if (step !== steps.done) autoLoadDoneRef.current = false; // done에서 벗어나면 리셋
      return;
    }
    if (autoLoadDoneRef.current) return;
    autoLoadDoneRef.current = true;
    loadSupportChat();      // 대화방 메시지 로드
    loadAttendanceHistory(); // 등록이력 로드
  }, [step, typeParam, apiAddr, loadAttendanceHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  // 채팅 입력/수신 시 하단에 바로 보이도록 스크롤
  useEffect(() => {
    const el = supportChatListRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [supportChatMessages]);

  const getloc = () => {
    if (!navigator.geolocation) return;
    const options = { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 };
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        sessionStorage.glat = pos.coords.latitude;
        sessionStorage.glon = pos.coords.longitude;
      },
      () => {},
      options
    );
  };

  const supportUploadBtnClickHandler = () => {
    if (typeof sessionStorage !== "undefined") sessionStorage.inputedFileSort = "sf";
    setSupportInputClicked(+new Date());
  };
  const supportSendBtnClickHandler = () => {
    setSupportSendClicked(+new Date());
  };

  const sendSupportChat = async () => {
    const isFileSend = (supportFilename || "").trim() && typeof sessionStorage !== "undefined" && sessionStorage.inputedFileSort === "sf";
    if (isFileSend) {
      getloc();
      if (!window.confirm("파일을 전송하시겠습니까?")) return;
      supportSendBtnClickHandler();
      const fn = (supportFilename || "").trim();
      const param = {
        name: chatDisplayName,
        msg: fn || "(파일)",
        dsid,
        msgsort: CSORT_SUPPORT,
        filename: fn,
      };
      socket.emit("chatting_support", { ...param, filename: param.filename });
      try {
        await fetch(`${apiAddr}/post/inputchatmsg`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...param, title: "", lat: sessionStorage.glat || "", lon: sessionStorage.glon || "" }),
        });
      } catch (e) {
        console.error(e);
      }
      setSupportChatInput("");
      setSupportFilename("");
      if (typeof sessionStorage !== "undefined") sessionStorage.fileSendOx = "false";
      return;
    }
    const msg = (supportChatInput || "").trim();
    if (!msg || !chatDisplayName || !apiAddr) return;
    setSupportChatInput("");
    const param = {
      name: chatDisplayName,
      msg,
      dsid,
      msgsort: CSORT_SUPPORT,
      filename: "",
    };
    socket.emit("chatting_support", param);
    try {
      await fetch(`${apiAddr}/post/inputchatmsg`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...param, title: "", lat: "", lon: "" }),
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleInsaLookup = async () => {
    const code = insaCode.trim();
    if (!code || !apiAddr) return;
    try {
      const res = await fetch(`${apiAddr}/get/insa?i_code=${encodeURIComponent(code)}`);
      const data = await res.json();
      if (data && !data.result && !data.message) {
        setPname(data.i_name || "");
        setPorg(data.fc_org_nm || "");
        setPrank(data.i_rank || "");
      }
    } catch (e) {}
  };

  const handleSubmit = async () => {
    if (!apiAddr || !dsid || locationId == null) return;
    setSubmitError("");
    setSubmitLoading(true);
    try {
      // site 모드: rdv_id=0 + site_id 함께 전송 (백엔드가 site_id 우선 처리)
      const locationField = effectiveIsSiteMode
        ? { rdv_id: 0, site_id: effectiveSiteId }
        : { rdv_id: effectiveRdvId };
      const body =
        step === steps.fireForm
          ? {
              dsid,
              ...locationField,
              person_type: "fire",
              pname: pname.trim(),
              porg: porg.trim(),
              pdept: pdept.trim(),
              prank: prank.trim(),
              insa_code: insaCode.trim(),
              pphone: pphone.trim(),
              duty_desc: dutyDesc.trim(),
            }
          : {
              dsid,
              ...locationField,
              person_type: "other",
              pname: pname.trim(),
              porg: porg.trim(),
              pdept: pdept.trim(),
              prank: prank.trim(),
              pphone: pphone.trim(),
              duty_desc: dutyDesc.trim(),
            };
      const res = await fetch(`${apiAddr}/post/rdvcheckin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.result === "success") {
        setCompletedAction("checkin");
        setStep(steps.done);
        socket.emit("rdvattendance", { dsid });
        let displayName = "";
        if (step === steps.otherForm) {
          displayName = [porg, pdept, pname].map((s) => (s || "").trim()).filter(Boolean).join(" ") || pname.trim() || "지원인력";
          setChatDisplayName(displayName);
        }
        try {
          localStorage.setItem(
            RDV_ATTEND_STORAGE_KEY,
            JSON.stringify({
              dsid,
              [locationIdKey]: locationId,
              dsnm,
              rname,
              typeParam,
              person_type: step === steps.fireForm ? "fire" : "other",
              pname: pname.trim(),
              pphone: pphone.trim(),
              porg: porg.trim(),
              pdept: pdept.trim(),
              prank: prank.trim(),
              insa_code: insaCode.trim(),
              duty_desc: dutyDesc.trim(),
              chatDisplayName: displayName,
              completedAction: "checkin",
            })
          );
        } catch (e) {
          console.warn("rdv attend save failed", e);
        }
      } else {
        setSubmitError(data.message || "출근 처리에 실패했습니다.");
      }
    } catch (e) {
      setSubmitError("전송 중 오류가 발생했습니다.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCheckOut = async () => {
    if (!apiAddr || !dsid || locationId == null) return;
    setSubmitError("");
    setCheckoutLoading(true);
    try {
      const locParam = effectiveIsSiteMode
        ? `site_id=${locationId}`
        : `rdv_id=${locationId}`;
      const res = await fetch(
        `${apiAddr}/get/rdvattendance?dsid=${encodeURIComponent(dsid)}&${locParam}`
      );
      const list = await res.json();
      const match =
        step === steps.fireForm
          ? Array.isArray(list)
            ? list.find(
                (r) =>
                  r.person_type === "fire" &&
                  !r.check_out_dt &&
                  ((insaCode.trim() && r.insa_code && String(r.insa_code).trim() === insaCode.trim()) ||
                    (String(r.pname || "").trim() === pname.trim() && String(r.pphone || "").trim() === pphone.trim()))
              )
            : null
          : Array.isArray(list)
            ? list.find(
                (r) =>
                  r.person_type === "other" &&
                  String(r.pname || "").trim() === pname.trim() &&
                  String(r.pphone || "").trim() === pphone.trim() &&
                  !r.check_out_dt
              )
            : null;
      if (!match) {
        setSubmitError("퇴근 처리할 출근 기록이 없습니다.");
        return;
      }
      const outRes = await fetch(`${apiAddr}/post/rdvcheckout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ snum: match.snum }),
      });
      const outData = await outRes.json();
      if (outData.result === "success") {
        setCompletedAction("checkout");
        setStep(steps.done);
        socket.emit("rdvattendance", { dsid });
        // 퇴근 상태 localStorage에 갱신 (페이지 재방문 시 퇴근 완료 상태 복원)
        try {
          const prev = JSON.parse(localStorage.getItem(RDV_ATTEND_STORAGE_KEY) || "{}");
          localStorage.setItem(RDV_ATTEND_STORAGE_KEY, JSON.stringify({ ...prev, completedAction: "checkout" }));
        } catch (e) {}
      } else {
        setSubmitError(outData.message || "퇴근 처리에 실패했습니다.");
      }
    } catch (e) {
      setSubmitError("퇴근 처리 중 오류가 발생했습니다.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!isValid) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f5f5f5",
        }}
      >
        <Paper sx={{ p: 3, maxWidth: 400, textAlign: "center" }}>
          <Typography color="error">
            잘못된 접속입니다. QR 코드로 다시 접속해 주세요.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const canSubmitFire = step === steps.fireForm && pname.trim() !== "";
  const canSubmitOther =
    step === steps.otherForm &&
    pname.trim() !== "";

  const titleBarText = [dsnm, rname].filter(Boolean).concat(effectiveIsSiteMode ? "재난현장 지원인력 관리" : "지원인력 관리").join(" / ");

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 480,
        minHeight: "100vh",
        bgcolor: "#f5f5f5",
        mx: "auto",
        pb: 2,
        boxSizing: "border-box",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* 모바일 상단 제목표시줄: 재난명 + 자원집결지명 + 지원인력 관리 */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          px: 2,
          py: 1.5,
          bgcolor: green[700],
          color: "white",
          boxShadow: 1,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: "center" }}>
          {titleBarText || "지원인력 관리"}
        </Typography>
      </Box>

      {typeParam === "other" && (
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "background.paper", px: 1 }}
        >
          <Tab label="등록" />
          <Tab label="대화방" disabled={!chatDisplayName} />
          <Tab label="등록이력" />
        </Tabs>
      )}

      <Box sx={{ p: 2, flex: 1 }}>
      {prefillLoading && attParam && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 3, gap: 1 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            등록 정보를 불러오는 중…
          </Typography>
        </Box>
      )}
      {(prefillFailed || (!locationReady && attParam && !prefillLoading)) && (
        <Paper sx={{ p: 3, mt: 2, textAlign: "center" }}>
          <Typography color="error" sx={{ mb: 1 }}>
            등록 정보를 불러오지 못했습니다.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            링크가 만료되었거나 서버가 최신 버전이 아닐 수 있습니다. 담당자에게 다시 문자 발송을 요청해 주세요.
          </Typography>
        </Paper>
      )}
      {step === steps.ask && typeParam !== "other" && locationReady && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            sx={{
              py: 3,
              fontSize: "1.1rem",
              bgcolor: green[700],
              "&:hover": { bgcolor: green[800] },
            }}
            onClick={() => setStep(steps.fireForm)}
          >
            강원 소방공무원
          </Button>
          <Button
            fullWidth
            variant="outlined"
            size="large"
            sx={{ py: 3, fontSize: "1.1rem" }}
            onClick={() => setStep(steps.otherForm)}
          >
            그외 인력
          </Button>
        </Stack>
      )}

      {step === steps.fireForm && (
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography sx={{ mb: 1, fontWeight: 600 }}>
            기본인적사항 (소방공무원)
          </Typography>
          <TextField fullWidth size="small" label="기관" value={porg} onChange={(e) => setPorg(e.target.value)} sx={{ mb: 1.5 }} />
          <TextField fullWidth size="small" label="부서" value={pdept} onChange={(e) => setPdept(e.target.value)} sx={{ mb: 1.5 }} />
          <TextField fullWidth size="small" label="계급" value={prank} onChange={(e) => setPrank(e.target.value)} sx={{ mb: 1.5 }} />
          <TextField fullWidth size="small" label="성명 *" required value={pname} onChange={(e) => setPname(e.target.value)} sx={{ mb: 1.5 }} />
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 1.5 }}>
            <TextField fullWidth size="small" label="후대번호(인사코드)" value={insaCode} onChange={(e) => setInsaCode(e.target.value)} placeholder="선택" />
            <Button variant="outlined" size="small" onClick={handleInsaLookup} disabled={!insaCode.trim()} sx={{ flexShrink: 0, mt: 0.5 }}>
              조회
            </Button>
          </Box>
          <TextField fullWidth size="small" label="연락처" value={pphone} onChange={(e) => setPphone(e.target.value)} sx={{ mb: 2 }} />
          <Typography sx={{ mb: 0.5, fontWeight: 600 }}>
            수행임무
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            placeholder="수행임무를 입력하세요"
            value={dutyDesc}
            onChange={(e) => setDutyDesc(e.target.value)}
            sx={{ mb: 2 }}
          />
          {submitError && (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {submitError}
            </Typography>
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button variant="outlined" onClick={() => setStep(steps.ask)}>
              뒤로
            </Button>
            <Button
              variant="contained"
              sx={{ bgcolor: green[700], "&:hover": { bgcolor: green[800] } }}
              onClick={handleSubmit}
              disabled={!canSubmitFire || submitLoading || checkoutLoading}
            >
              {submitLoading ? <CircularProgress size={24} /> : "출근 등록"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<LogoutIcon />}
              onClick={handleCheckOut}
              disabled={submitLoading || checkoutLoading}
              sx={{ borderColor: green[600], color: green[700], "&:hover": { borderColor: green[700], bgcolor: green[50] } }}
            >
              {checkoutLoading ? <CircularProgress size={24} /> : "퇴근 등록"}
            </Button>
          </Stack>
        </Paper>
      )}

      {step === steps.otherForm && (typeParam !== "other" || activeTab === 0) && locationReady && !(prefillLoading && attParam) && (
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography sx={{ mb: 1, fontWeight: 600 }}>
            기본인적사항 (그 외)
          </Typography>
          {typeParam === "other" && !attParam && (
            <Typography
              variant="body2"
              sx={{ mb: 1.5, color: "warning.dark", bgcolor: "#fff8e1", p: 1, borderRadius: 1 }}
            >
              사전 등록 정보가 연결되지 않은 링크입니다. 담당자에게 새 문자(연동 링크) 발송을 요청하거나, 아래에 직접 입력해 주세요.
            </Typography>
          )}
          {alreadyCheckedIn && (
            <Typography
              variant="body2"
              sx={{ mb: 1.5, color: green[800], bgcolor: green[50], p: 1, borderRadius: 1 }}
            >
              출근 등록이 완료되어 있습니다. 정보 확인 후 필요 시 퇴근 등록을 이용하세요.
            </Typography>
          )}
          {alreadyCheckedOut && (
            <Typography
              variant="body2"
              sx={{ mb: 1.5, color: "text.secondary", bgcolor: "#f5f5f5", p: 1, borderRadius: 1 }}
            >
              퇴근 처리가 완료된 인원입니다.
            </Typography>
          )}
          <TextField fullWidth size="small" label="기관" value={porg} onChange={(e) => setPorg(e.target.value)} sx={{ mb: 1.5 }} />
          <TextField fullWidth size="small" label="부서" value={pdept} onChange={(e) => setPdept(e.target.value)} sx={{ mb: 1.5 }} />
          <TextField fullWidth size="small" label="계급" value={prank} onChange={(e) => setPrank(e.target.value)} sx={{ mb: 1.5 }} />
          <TextField fullWidth size="small" label="성명 *" required value={pname} onChange={(e) => setPname(e.target.value)} sx={{ mb: 1.5 }} />
          <TextField fullWidth size="small" label="연락처" value={pphone} onChange={(e) => setPphone(e.target.value)} sx={{ mb: 2 }} />
          <Typography sx={{ mb: 0.5, fontWeight: 600 }}>
            수행임무
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            placeholder="수행임무를 입력하세요"
            value={dutyDesc}
            onChange={(e) => setDutyDesc(e.target.value)}
            sx={{ mb: 2 }}
          />
          {submitError && (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {submitError}
            </Typography>
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button variant="outlined" onClick={() => setStep(steps.ask)} disabled={typeParam === "other"}>
              뒤로
            </Button>
            <Button
              variant="contained"
              sx={{ bgcolor: green[700], "&:hover": { bgcolor: green[800] } }}
              onClick={handleSubmit}
              disabled={
                !canSubmitOther ||
                submitLoading ||
                checkoutLoading ||
                alreadyCheckedIn ||
                alreadyCheckedOut
              }
            >
              {submitLoading ? <CircularProgress size={24} /> : "출근 등록"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<LogoutIcon />}
              onClick={handleCheckOut}
              disabled={
                submitLoading ||
                checkoutLoading ||
                (!alreadyCheckedIn && !attParam && !pname.trim())
              }
              sx={{ borderColor: green[600], color: green[700], "&:hover": { borderColor: green[700], bgcolor: green[50] } }}
            >
              {checkoutLoading ? <CircularProgress size={24} /> : "퇴근 등록"}
            </Button>
          </Stack>
        </Paper>
      )}

      {step === steps.done && (typeParam !== "other" || activeTab === 0) && (
        <Paper sx={{ p: 3, mt: 2, textAlign: "center" }}>
          <Typography variant="h6" sx={{ color: green[700], mb: 1 }}>
            {completedAction === "checkout" ? "퇴근 등록이 완료되었습니다." : "출근 등록이 완료되었습니다."}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {completedAction === "checkout" ? "수고하셨습니다." : "담당자 안내에 따라 활동해 주세요."}
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              sx={{ borderColor: green[600], color: green[700], "&:hover": { borderColor: green[700], bgcolor: green[50] } }}
              onClick={() => {
                autoLoadDoneRef.current = false; // 재등록 후 다시 자동 로드 허용
                setStep(typeParam === "other" ? steps.otherForm : steps.fireForm);
              }}
            >
              등록수정
            </Button>
            <Button
              variant="contained"
              sx={{ bgcolor: green[700], "&:hover": { bgcolor: green[800] } }}
              onClick={handleSubmit}
              disabled={submitLoading || checkoutLoading}
            >
              {submitLoading ? <CircularProgress size={20} /> : "출근"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<LogoutIcon />}
              sx={{ borderColor: green[600], color: green[700], "&:hover": { borderColor: green[700], bgcolor: green[50] } }}
              onClick={handleCheckOut}
              disabled={submitLoading || checkoutLoading}
            >
              {checkoutLoading ? <CircularProgress size={20} /> : "퇴근"}
            </Button>
          </Stack>
        </Paper>
      )}

      {typeParam === "other" && activeTab === 2 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography sx={{ mb: 1, fontWeight: 600 }}>
            출퇴근 이력
          </Typography>
          {historyLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress />
            </Box>
          ) : attendanceHistory.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
              {pname.trim() || pphone.trim()
                ? "출퇴근 이력이 없습니다."
                : "등록을 완료한 뒤 이력이 표시됩니다."}
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: "60vh", overflow: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>출근 시각</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>퇴근 시각</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>임무</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attendanceHistory.map((row, i) => (
                    <TableRow key={row.snum != null ? row.snum : i}>
                      <TableCell>{row.check_in_dt ? new Date(row.check_in_dt).toLocaleString("ko-KR") : "-"}</TableCell>
                      <TableCell>{row.check_out_dt ? new Date(row.check_out_dt).toLocaleString("ko-KR") : "미퇴근"}</TableCell>
                      <TableCell>{(row.duty_desc || "").toString().trim() || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {typeParam === "other" && activeTab === 1 && (
        <Paper
          sx={{
            p: 0,
            mt: 2,
            display: "flex",
            flexDirection: "column",
            minHeight: 360,
            overflow: "hidden",
            borderRadius: 2,
            border: "1px solid",
            borderColor: green[700],
          }}
        >
          {!chatDisplayName ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center", fontSize: "1rem" }}>
              등록을 먼저 완료해 주세요. (출근 등록 후 대화방 이용 가능)
            </Typography>
          ) : (
            <>
              {/* 채팅 바디: 상단 제목표시줄과 동일 녹색 계열 */}
              <Box
                ref={supportChatListRef}
                sx={{
                  flex: 1,
                  minHeight: 260,
                  maxHeight: "60vh",
                  overflowY: "auto",
                  overflowX: "hidden",
                  p: 1,
                  bgcolor: green[50],
                }}
              >
                <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, "& > li": { mb: 1 } }}>
                  {supportChatMessages.map((m, i) => {
                    const nameParts = (m.name || "").trim().split(/\s+/);
                    const user_org = nameParts[0] || "";
                    const user_name = nameParts.slice(1).join(" ") || nameParts[0] || m.name || "";
                    const normalizeName = (s) => (String(s || "").trim()).replace(/\s+/g, " ");
                    const isSent = normalizeName(m.name) === normalizeName(chatDisplayName);
                    const fn = (m.filename || "").trim();
                    const filepath = fn ? `https://mfm.119survey.org/uploads/${fn}` : "";
                    const fileExt = fn ? (fn.split(".")[1] || "").toLowerCase() : "";
                    const isImage = ["jpg", "jpeg", "png", "gif", "bmp"].includes(fileExt);
                    const isVideo = fileExt === "mp4";
                    return (
                      <Box
                        component="li"
                        key={i}
                        sx={{
                          display: "flex",
                          flexDirection: isSent ? "row-reverse" : "row",
                          alignItems: "flex-end",
                          width: "95%",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            bgcolor: green[200],
                            border: "1px solid",
                            borderColor: green[700],
                            borderRadius: 1,
                            px: 0.75,
                            py: 0.25,
                            fontSize: "0.9rem",
                            fontWeight: 600,
                          }}
                        >
                          <span style={{ fontSize: "0.85rem" }}>{user_org}</span>
                          <span style={{ fontSize: "0.85rem" }}>{user_name}</span>
                        </Box>
                        <Box
                          sx={{
                            borderRadius: 1,
                            px: 1,
                            py: 0.5,
                            mx: 0.5,
                            flex: 1,
                            bgcolor: isSent ? green[100] : green[200],
                            fontSize: "1rem",
                          }}
                        >
                          {m.msg && <span>{m.msg}</span>}
                          {fn && (
                            <>
                              {m.msg && " "}
                              {isImage ? (
                                <img
                                  src={filepath}
                                  alt={fn}
                                  width={100}
                                  height={100}
                                  style={{ cursor: "pointer", verticalAlign: "middle", display: "block", marginTop: 4 }}
                                  onClick={() => window.open(filepath, "_blank")}
                                />
                              ) : isVideo ? (
                                <video src={filepath} width={100} height={100} controls style={{ display: "block", marginTop: 4 }} />
                              ) : (
                                <a href={filepath} target="_blank" rel="noopener noreferrer" style={{ display: "block", marginTop: 4 }}>{fn}</a>
                              )}
                            </>
                          )}
                        </Box>
                        <Typography component="span" sx={{ fontSize: "0.85rem", color: "text.secondary", flexShrink: 0 }}>
                          {m.time}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              {/* 입력 영역: 상단 제목표시줄과 동일 녹색 계열, 파일첨부 지원 */}
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{
                  p: 0.5,
                  bgcolor: green[200],
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <FileUpload
                  fileInputId="file_support_mobile"
                  apiAddr={apiAddr}
                  inputclicked={supportInputClicked}
                  inputFilename={(fn) => {
                    setSupportFilename(fn || "");
                    setSupportChatInput(fn || "");
                    if (typeof sessionStorage !== "undefined") {
                      sessionStorage.fileSendOx = fn ? "true" : "false";
                      sessionStorage.inputedFileSort = "sf";
                    }
                    if (fn === "") {
                      setSupportChatInput("");
                    }
                  }}
                  sendclicked={supportSendClicked}
                  sendBtnClickHandler={supportSendBtnClickHandler}
                />
                <Button
                  variant="outlined"
                  size="small"
                  aria-label="파일 첨부"
                  onClick={supportUploadBtnClickHandler}
                  sx={{
                    borderColor: green[700],
                    color: green[800],
                    minWidth: 40,
                    "&:hover": { borderColor: green[800], bgcolor: green[50] },
                  }}
                >
                  <UploadIcon fontSize="small" />
                </Button>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="메시지 또는 파일 첨부"
                  value={supportChatInput}
                  onChange={(e) => setSupportChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendSupportChat()}
                  sx={{
                    "& .MuiInputBase-root": { bgcolor: "white", borderRadius: 1 },
                    "& .MuiInputBase-input": { fontSize: "1rem", py: 0.75 },
                  }}
                />
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: green[700],
                    color: "white",
                    "&:hover": { bgcolor: green[800] },
                    minWidth: 48,
                  }}
                  onClick={sendSupportChat}
                  disabled={!supportChatInput.trim() && !supportFilename}
                >
                  <SendIcon />
                </Button>
              </Stack>
            </>
          )}
        </Paper>
      )}
      </Box>

      {/* 푸터 */}
      <Box
        component="footer"
        sx={{
          mt: "auto",
          py: 2,
          px: 2,
          textAlign: "center",
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          강원특별자치도 소방본부
        </Typography>
      </Box>

      {/* 모바일 즐겨찾기(홈화면 추가) 플로팅 버튼 */}
      <Fab
        color="primary"
        aria-label="홈 화면에 추가"
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          bgcolor: green[700],
          "&:hover": { bgcolor: green[800] },
          display: { xs: "flex", sm: "flex" },
        }}
        onClick={handleAddToFavorites}
      >
        <StarBorderIcon />
      </Fab>
      <Snackbar
        open={favSnackOpen}
        autoHideDuration={4000}
        onClose={() => setFavSnackOpen(false)}
        message="이 페이지를 홈 화면에 추가하시면 빠르게 접속할 수 있습니다. 브라우저 메뉴에서 '홈 화면에 추가' 또는 '즐겨찾기에 추가'를 선택해 주세요."
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
